<script lang="ts">
  /// 弁護士 複数選択 (86eya0dva PR4: 契約の related_lawyer_ids 編集用)。
  /// AccountMultiSelect と同じ形 (チップ + 追加ドロップダウン)。候補は firmId 指定時は
  /// その事務所所属のみ、未指定なら先頭ページ (page_size=200) を自前 fetch する
  /// (LawyerSelect と同じ取得方針)。
  import { listLawyers } from '@shared/api/lawyers';
  import { reqArray, reqRecord } from '@shared/value-objects/parse-helpers';

  interface LawyerOption {
    id: string;
    full_name: string;
  }

  interface Props {
    /// 選択中の lawyer id 配列。
    value: string[];
    /// 指定時はその事務所所属の弁護士のみを候補にする。
    firmId?: string;
    disabled?: boolean;
    /// 選択変更時に呼ばれる任意コールバック。新しい配列を渡す。
    onChange?: (value: string[]) => void;
  }

  let {
    value = $bindable([]),
    firmId,
    disabled = false,
    onChange,
  }: Props = $props();

  let lawyers = $state<LawyerOption[]>([]);
  let ready = $state(false);
  let error = $state<string | null>(null);
  let pending = $state('');

  let lastFetchedFirmId: string | undefined = undefined;
  $effect(() => {
    if (firmId === lastFetchedFirmId) return;
    lastFetchedFirmId = firmId;
    void load();
  });

  async function load() {
    ready = false;
    error = null;
    try {
      const res = await listLawyers({
        firm_id: firmId,
        page_size: 200,
        sort_by: 'full_name',
        sort_dir: 'asc',
      });
      const items = reqArray(reqRecord(res, 'lawyers').items, 'lawyers.items');
      lawyers = items.map((it) => {
        const o = reqRecord(it, 'lawyer');
        return {
          id: String(o.id ?? ''),
          full_name: String(o.full_name ?? ''),
        };
      });
    } catch (err) {
      error = err instanceof Error ? err.message : String(err);
      lawyers = [];
    } finally {
      ready = true;
    }
  }

  // まだ選択されていない候補のみを追加ドロップダウンに出す。
  let addable = $derived(lawyers.filter((l) => !value.includes(l.id)));

  // 選択中 id を候補に解決 (候補外でも id をそのまま表示し、永続済み id が黙って消えないようにする)。
  let selected = $derived(
    value.map((id) => ({
      id,
      lawyer: lawyers.find((l) => l.id === id) ?? null,
    })),
  );

  function add() {
    if (!pending || value.includes(pending)) {
      pending = '';
      return;
    }
    value = [...value, pending];
    pending = '';
    onChange?.(value);
  }

  function remove(id: string) {
    value = value.filter((v) => v !== id);
    onChange?.(value);
  }
</script>

<div class="multi">
  {#if selected.length > 0}
    <ul class="chips">
      {#each selected as item (item.id)}
        <li class="chip">
          <span class="chip-label">{item.lawyer?.full_name ?? item.id}</span>
          {#if !disabled}
            <button
              type="button"
              class="chip-remove"
              aria-label="弁護士を外す"
              onclick={() => remove(item.id)}>×</button
            >
          {/if}
        </li>
      {/each}
    </ul>
  {/if}

  {#if !disabled}
    <select bind:value={pending} onchange={add} {disabled}>
      <option value="">弁護士を追加…</option>
      {#each addable as l (l.id)}
        <option value={l.id}>{l.full_name}</option>
      {/each}
    </select>
  {/if}

  {#if !ready}
    <small class="hint">読込中…</small>
  {/if}
  {#if error}
    <small class="error">弁護士の取得に失敗しました: {error}</small>
  {/if}
  {#if ready && selected.length === 0 && disabled}
    <span class="muted">—</span>
  {/if}
</div>

<style>
  .multi {
    display: flex;
    flex-direction: column;
    gap: 6px;
  }
  .chips {
    display: flex;
    flex-wrap: wrap;
    gap: 4px;
    list-style: none;
    margin: 0;
    padding: 0;
  }
  .chip {
    display: inline-flex;
    align-items: center;
    gap: 4px;
    background: #eef2ff;
    color: #3730a3;
    padding: 2px 4px 2px 8px;
    border-radius: 999px;
    font-size: 0.75rem;
  }
  .chip-remove {
    border: 0;
    background: transparent;
    color: inherit;
    cursor: pointer;
    font-size: 0.85rem;
    line-height: 1;
    padding: 0 2px;
  }
  select {
    padding: 6px 8px;
    border: 1px solid #d1d5db;
    border-radius: 4px;
    font: inherit;
    background: #fff;
    align-self: flex-start;
  }
  .hint {
    color: #6b7280;
    font-size: 0.7rem;
  }
  .error {
    color: #b91c1c;
    font-size: 0.7rem;
  }
  .muted {
    color: #6b7280;
    font-size: 0.875rem;
  }
</style>
